/**
 * Pre-save validation for data-list activity questions.
 *
 * A question of this type cannot be saved without a duration or an interval:
 * the person in the field would have nothing to build their workday from. The
 * builder has no per-question save, so the block lives in the whole-form save,
 * next to the repeated section-name validation.
 */

export const ACTIVITY_PERIOD_TIME_TYPE = 'select_activity_period_time';

/**
 * Sentinel `executeSave` rejects with when there are incomplete activity
 * questions. The header wraps the save in a try/catch that shows a generic
 * "save error"; without this marker the user would get two messages, the
 * specific one and the generic one, and the second only adds noise.
 * Follows the pattern the header already uses for `nameExists`.
 */
export const ACTIVITY_PERIOD_TIME_INCOMPLETE = 'activityPeriodTimeIncomplete';

interface QuestionLike {
  id?: string | number;
  name?: string;
  type?: string;
  workingTime?: string;
  intervalDefault?: string;
  questions?: QuestionLike[];
}

export interface InvalidActivityQuestion {
  id: string;
  name: string;
  /** Name of the repeatable question containing it, when nested. */
  tableName?: string;
  reason: 'missing-duration' | 'missing-interval';
}

const isBlank = (value?: string): boolean => value === undefined || value === null || value === '';

/** A configured but zero duration is invalid the same as an empty one. */
const hasValidDuration = (workingTime?: string): boolean => {
  if (isBlank(workingTime)) return false;
  const minutes = parseInt(workingTime as string, 10);
  return !Number.isNaN(minutes) && minutes > 0;
};

/**
 * Walks the questions and returns the activity ones that cannot be saved.
 *
 * Descends into the sub-questions of repeatables: the configuration is also
 * rendered inside a table, so a badly configured nested question breaks the
 * form just like a standalone one. The pre-existing section validation only
 * looks at the top level and is not a usable base for this.
 */
export const findInvalidActivityQuestions = (
  questions: QuestionLike[] | undefined,
): InvalidActivityQuestion[] => {
  if (!Array.isArray(questions)) return [];

  const collect = (list: QuestionLike[], tableName?: string): InvalidActivityQuestion[] =>
    list.flatMap((question) => {
      if (question?.type === 'table') {
        return collect(question.questions ?? [], question.name);
      }

      if (question?.type !== ACTIVITY_PERIOD_TIME_TYPE) return [];

      const reason: InvalidActivityQuestion['reason'] | null = !hasValidDuration(
        question.workingTime,
      )
        ? 'missing-duration'
        : isBlank(question.intervalDefault)
          ? 'missing-interval'
          : null;

      if (!reason) return [];

      return [
        {
          id: String(question.id ?? ''),
          name: question.name ?? '',
          ...(tableName ? { tableName } : {}),
          reason,
        },
      ];
    });

  return collect(questions);
};

export const hasInvalidActivityQuestions = (questions: QuestionLike[] | undefined): boolean =>
  findInvalidActivityQuestions(questions).length > 0;
